import React, { MutableRefObject, Suspense } from 'react';
import { getAddress } from 'viem';
import { useAccount } from 'wagmi';
import { Dialog, DialogContent, DialogTitle } from '../components/ShadeDialog';
import PlayerReviewerContent from './PlayerReviewerContent';
import { PlayerReviewerLoading } from './PlayerReviewerContent';
import { Board, Place, PlaceId, SlapId, StickerDesignId } from '../utils/Primitives';
import GetTheLink from '../components/GetTheLink';
import { pridi } from '../fonts';

type PlayerReviewerProps = {
  playerAddress: string | null;
  boardRef: MutableRefObject<Board>;
  onClose: () => void;
  handleReviewPlace: (placeId: PlaceId) => void;
  handleReviewSticker: (stickerId: StickerDesignId) => void;
  handleReviewSlap: (slapId: SlapId) => void;
  startPayoutView: () => void;
  startPublishingView: () => void;
  portalContainer?: HTMLElement | null;
};

export default function PlayerReviewer({
  playerAddress,
  boardRef,
  onClose,
  handleReviewPlace,
  handleReviewSticker,
  handleReviewSlap,
  startPayoutView,
  startPublishingView,
  portalContainer,
}: PlayerReviewerProps) {
  const { address: loggedInAddress } = useAccount();

  const normalizedPlayerAddress = playerAddress
    ? getAddress(playerAddress)
    : null;

  const normalizedLoggedInAddress = loggedInAddress
    ? getAddress(loggedInAddress)
    : null;

  const showingLoggedInPlayer =
    normalizedPlayerAddress && normalizedLoggedInAddress
      ? normalizedPlayerAddress === normalizedLoggedInAddress
      : false;

  const title = showingLoggedInPlayer ? 'Your Account' : 'Account Details';

  return (
    <Dialog open={playerAddress !== null} onOpenChange={onClose}>
      <DialogContent
        className="bg-white text-slate-900 p-0 overflow-hidden"
        portalContainer={portalContainer}
      >
        <div className="sticky top-0 z-10 border-b border-slate-200 bg-white px-4 py-3">
          <div className="flex items-center gap-2">
            <DialogTitle className={`text-2xl ${pridi.className}`}>{title}</DialogTitle>
            {normalizedPlayerAddress && (
              <GetTheLink
                placeId={null}
                slapId={null}
                stickerDesignId={null}
                playerAddress={normalizedPlayerAddress}
              />
            )}
          </div>
        </div>
        <div className="max-h-[70vh] overflow-y-auto px-4 py-3">
          <Suspense fallback={<PlayerReviewerLoading />}>
            <PlayerReviewerContent
              playerAddress={normalizedPlayerAddress}
              boardRef={boardRef}
              handleReviewPlace={handleReviewPlace}
              handleReviewSticker={handleReviewSticker}
              handleReviewSlap={handleReviewSlap}
              startPayoutView={startPayoutView}
              startPublishingView={startPublishingView}
              onClose={onClose}
            />
          </Suspense>
        </div>
      </DialogContent>
    </Dialog>
  );
}
